import * as React from 'react'
import { WrappedComponentProps, injectIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import { Elements, TextButton } from '@tfo/common'
import DenyApprovalModal from '../../../../components/modals/deny-approval-modal/DenyApprovalModal'
import $http from '../../../../utils/$http'
import { ApprovableOrderViewModel, IndividualInformationViewModel } from '../../ViewModels'
import AboutBackgroundCheck from '../../common/about-background-check/AboutBackgroundCheck'
import IndividualInformationEditForm from './IndividualInformation'

const { Paragraph } = Elements

type LegitimateInterestAssessmentInformationProps = ApprovableOrderViewModel & {
    isLoading: boolean
    setIsLoading: (state: boolean) => void
}

const LegitimateInterestAssessmentInformation: React.FunctionComponent<LegitimateInterestAssessmentInformationProps &
    WrappedComponentProps> = (props) => {
        const { companyName, orderId, isLoading, setIsLoading } = props
        const history = useHistory()
        const canUseBankIdSign = props.approval.acceptanceMethod === "BankId"

        const [updatedIndividualInfo, setUpdatedIndividualInfo] = React.useState<IndividualInformationViewModel>()
        const [validIndividualInfo, setValidIndividualInfo] = React.useState<boolean>(true)
        const [showDenyApprovalModal, setShowDenyApprovalModal] = React.useState<boolean>(false)
        const [showNetworkError, setShowNetworkError] = React.useState<boolean>(false)

        const hasIndividualInfoChanged = () => {
            if (!updatedIndividualInfo) {
                return false
            }

            return props.individualFirstName !== updatedIndividualInfo.individualFirstName
                || props.individualLastName !== updatedIndividualInfo.individualLastName
                || props.individualEmail !== updatedIndividualInfo.individualEmail
                || props.individualPhoneNumber !== updatedIndividualInfo.individualPhoneNumber
        }

        const applyApproval = (action: "approve" | "deny") => {
            setIsLoading(true)

            $http.post(`${action}/${orderId}`, updatedIndividualInfo)
                .then(() => {
                    setShowNetworkError(false)
                    //go back to /candidate using history.goback() (edge case: if go back isn't /candidate)
                    history.goBack();
                })
                .catch(() => {
                    setShowNetworkError(true)
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }

        const acceptOrder = () => applyApproval("approve")
        const denyOrder = () => applyApproval("deny")

        const disableExecuteActions = isLoading || !validIndividualInfo
        const displayUpdatedInfoWarning = validIndividualInfo && hasIndividualInfoChanged()

        return <>
            <div className="AboutBackgroundCheck">
                <Paragraph>
                    {
                        props.intl.formatMessage(
                            { id: 'startIndividual.body' },
                            { companyName })
                    }
                    <b>
                        {
                            props.intl.formatMessage({ id: 'startIndividual.body.bold' })
                        }
                    </b>
                </Paragraph>
            </div>
            <div className="AboutBackgroundCheck">
                <Paragraph>
                    {props.intl.formatMessage(
                        { id: 'startIndividual.body.partTwo' },
                        {
                            companyName: companyName,
                            link: (
                                <a
                                    href={props.intl.formatMessage({ id: 'common.tfoGdprPolicy.link-url' })}
                                    target='_blank' rel="noreferrer">
                                    {props.intl.formatMessage({ id: 'common.tfoGdprPolicy' })}{' '}
                                </a>
                            ),
                        },
                    )}
                </Paragraph>
            </div>
            <div className="AboutBackgroundCheck">
                <Paragraph>
                    {props.intl.formatMessage(
                        { id: 'startIndividual.body.part.deletion' },
                        { companyName: companyName }
                    )}
                </Paragraph>
            </div>
            <AboutBackgroundCheck order={props} />
            <div className="AboutBackgroundCheck">
                <Paragraph className="confirm-individual-information">
                    {props.intl.formatMessage(
                        { id: 'aboutbackgroundcheck.confirmIndividualInfo' },
                        { companyName: companyName })}
                </Paragraph>
                <IndividualInformationEditForm
                    individualInfo={props}
                    onChange={(u, v) => {
                        setUpdatedIndividualInfo(u)
                        setValidIndividualInfo(v)
                    }}
                />
                {!validIndividualInfo && <Paragraph className='InputErrorMessage'>
                    {props.intl.formatMessage(
                        { id: 'aboutbackgroundcheck.confirmIndividualInfo.error' },
                        { companyName: companyName })}
                </Paragraph>}
                {displayUpdatedInfoWarning && <div className="AboutBackgroundCheck">
                    <Paragraph className='updated-individual-info-notification'>
                        {props.intl.formatMessage(
                            { id: 'aboutbackgroundcheck.confirmIndividualInfo.update' })}
                    </Paragraph>
                </div>}
            </div>
            <div className="AboutBackgroundCheck">
                <Paragraph>
                    {props.intl.formatMessage(
                        { id: 'aboutbackgroundcheck.beforeProceeding.4' },
                        {
                            company: companyName,
                            link: (
                                <a
                                    href={props.intl.formatMessage({ id: 'common.tfoGdprPolicy.link-url' })}
                                    target='_blank' rel="noreferrer" >
                                    {props.intl.formatMessage({ id: 'common.tfoGdprPolicy' })}{' '}
                                </a>
                            ),
                        },
                    )}
                </Paragraph>
            </div>
            {<div className={'IndividualButtons'} >
                <TextButton
                    secondary={true}
                    disabled={disableExecuteActions}
                    className="StartIndividual__TextButton StartIndividual__TextButton--left"
                    onClick={() => {
                        setShowDenyApprovalModal(true)
                    }}>
                    {props.intl.formatMessage({ id: 'startIndividual.btn.deny' })}
                </TextButton>
                {!canUseBankIdSign &&
                    <TextButton
                        className="StartIndividual__TextButton StartIndividual__TextButton--right"
                        disabled={disableExecuteActions}
                        onClick={acceptOrder}>
                        {props.intl.formatMessage({
                            id: 'startIndividual.btn.approve',
                        })}
                    </TextButton>
                }

                {canUseBankIdSign && <TextButton
                    className="StartIndividual__TextButton StartIndividual__TextButton--right"
                    disabled={disableExecuteActions}
                    onClick={() => history.push(`/approval/${orderId}/bankid`)}>
                    {props.intl.formatMessage({
                        id: 'startIndividual.bankid.btn.approve',
                    })}
                </TextButton>}
            </div>}
            <div className="AboutBackgroundCheck">
                {showNetworkError &&
                    <div className="InputErrorMessage">
                        {props.intl.formatMessage({ id: 'startIndividual.networkError' })}
                    </div>
                }
            </div>

            <div className="AboutBackgroundCheck">
                <Paragraph small >
                    {props.intl.formatMessage(
                        { id: 'startIndividual.footer' },
                        {
                            companyName: companyName,
                            link: (
                                <Link to="/faq">
                                    {props.intl.formatMessage({
                                        id: 'startIndividual.link.faq',
                                    })}
                                </Link>
                            ),
                        },
                    )}
                </Paragraph>
            </div>

            {showDenyApprovalModal ? (
                <DenyApprovalModal
                    onDenyClick={() => setShowDenyApprovalModal(false)}
                    onCloseClick={() => setShowDenyApprovalModal(false)}
                    onConfirmClick={denyOrder}
                    companyName={companyName}
                />
            ) : null}
        </>
    }

export default injectIntl(LegitimateInterestAssessmentInformation)